import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { ModalHeader } from '../../../molecules/ModalHeader/ModalHeader';
import { BreakdownBody, type BreakdownView } from '../BreakdownBody/BreakdownBody';
import styles from './BreakdownPanel.module.css';

/**
 * Competitors — `Modal` at `Type=Breakdown` (2235:120695), shown in the
 * conversations modal's card by `Breakdown ›` on a conversation row
 * (prototype `ConversationBreakdownModal`'s `BreakdownPanel`).
 *
 * **The container only.** The five widgets inside are `BreakdownBody`, shared
 * with `PostBreakdownDrawer`: Figma draws the identical stack in both, and only
 * the container and its header differ. Not a replacement for the drawer — the
 * two panels are the same content reached from two tables (confirmed with the
 * user 2026-09-26).
 *
 * Not in Figma: **the back chevron is a button.** The frame draws `chevron
 * left` in the header's decorative `Featured icon` slot; a back arrow that
 * cannot be pressed is a lie about where you are, so it is a real `ghostGrey`
 * icon button. It calls `onBack`, and falls back to `onClose`.
 */
export type BreakdownPanelProps = {
  /** Where the header's `‹` goes. Omit and it closes. */
  onBack?: () => void;
  onClose: () => void;
  /** The breakdown, once loaded. */
  view?: BreakdownView;
  /** Figma's `Name of the conversation` — the row's own text. */
  name?: string;
  /** Ids the shared dialog points `aria-labelledby` / `aria-describedby` at. */
  titleId: string;
  descriptionId: string;
};

export function BreakdownPanel({
  onClose,
  onBack,
  view,
  name,
  titleId,
  descriptionId,
}: BreakdownPanelProps) {
  /*
   * Whether the widgets overflow the slot — the scrim at the panel's foot shows
   * only then, because it is a scroll affordance. Measured rather than assumed,
   * and re-measured when the content changes as well as on resize.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrolling, setScrolling] = useState(false);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const measure = () => setScrolling(body.scrollHeight > body.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [view]);

  return (
    <div className={styles.panel}>
      <ModalHeader
        title="Breakdown"
        description={name}
        titleId={titleId}
        descriptionId={descriptionId}
        onClose={onClose}
        icon={
          /*
           * Figma's glyph is `chevron left` (2235:126798), whose single path is
           * the *same shape* as the file's older `left arrow` (123:5589), so
           * this uses the icon we already have. Flagged for the designer.
           */
          <Button
            hierarchy="ghostGrey"
            iconLeading={<Icon name="leftArrow" />}
            onClick={onBack ?? onClose}
            aria-label={onBack ? 'Back to conversations' : 'Close breakdown'}
          />
        }
      />
      <div className={styles.body} ref={bodyRef}>
        {view && <BreakdownBody view={view} noun="conversation" />}
      </div>
      {/*
        Figma `Overlay` (2246:32092) — the fade holding the panel's bottom edge
        while the widgets scroll under it. Decorative, so `aria-hidden`.
      */}
      {scrolling && <span className={styles.scrim} aria-hidden />}
    </div>
  );
}
